import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Activity } from "lucide-react";
import { api } from "@/lib/api";
import { returnCountdown } from "@/lib/format";

function sevColor(s) {
  if (s === "Ruled Out" || s === "Suspended") return "#F87171";
  if (s === "Doubtful") return "#FB923C";
  if (s === "Game-Time Decision") return "#FBBF24";
  return "#FFB020";
}

export default function Embed() {
  const [params] = useSearchParams();
  const key = params.get("key");
  const [league, setLeague] = useState(params.get("league") || "NFL");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!key) { setError("Missing embed key"); setLoading(false); return; }
    setLoading(true);
    api.get(`/partner/injuries?key=${encodeURIComponent(key)}&league=${league}`)
      .then((r) => { setData(r.data); setError(null); })
      .catch((e) => setError(e.response?.data?.detail || "Unable to load feed"))
      .finally(() => setLoading(false));
  }, [key, league]);

  const brand = data?.brand || {};
  const color = brand.color || "#00E5A3";
  const leagues = brand.leagues || ["NHL", "NBA", "NFL", "MLB"];

  return (
    <div data-testid="embed-board" className="min-h-screen bg-[#080B11] text-slate-200 px-4 py-4">
      <div className="max-w-[900px] mx-auto">
        <div className="flex items-center justify-between mb-3 pb-3 border-b border-[#232D42]">
          <div className="flex items-center gap-2.5 min-w-0">
            {brand.logo
              ? <img src={brand.logo} alt="" className="h-7 w-7 object-contain shrink-0" />
              : <Activity size={20} style={{ color }} className="shrink-0" />}
            <div className="min-w-0">
              <div className="font-bold text-slate-100 truncate" style={{ fontSize: 15 }}>{brand.name || "Injury Report"}</div>
              <div className="text-[10px] font-mono text-slate-500">Live injury & status report</div>
            </div>
          </div>
          <div className="flex gap-1.5">
            {leagues.map((lg) => (
              <button key={lg} data-testid={`embed-league-${lg}`} onClick={() => setLeague(lg)}
                className="px-2 py-1 rounded text-[11px] font-semibold transition-colors"
                style={league === lg ? { background: color, color: "#04140F" } : { background: "#151B2B", color: "#94A3B8" }}>
                {lg}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-500 text-sm">Loading…</div>
        ) : error ? (
          <div data-testid="embed-error" className="text-center py-12 text-slate-500 text-sm">{error}</div>
        ) : (data.teams || []).length === 0 ? (
          <div className="text-center py-12 text-slate-500 text-sm">No active {league} designations right now.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {data.teams.map((g, i) => (
              <div key={i} className="rounded-lg bg-[#0F1420] border border-[#232D42] p-3">
                <div className="flex items-center gap-2 mb-2">
                  {g.team?.logo && <img src={g.team.logo} alt="" className="h-6 w-6 object-contain" />}
                  <span className="text-sm font-bold text-slate-100 truncate">{g.team?.name}</span>
                  <span className="text-[10px] font-mono text-slate-500 ml-auto">{g.count}</span>
                </div>
                <div className="divide-y divide-[#1A2133]">
                  {g.players.map((p, j) => {
                    const rc = returnCountdown(p.return_date);
                    return (
                      <div key={j} className="py-1.5 flex items-center gap-2">
                        <span className="text-[13px] text-slate-100 truncate">{p.name}</span>
                        {p.position && <span className="text-[9px] font-mono text-slate-600">{p.position}</span>}
                        <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded ml-auto shrink-0"
                          style={{ color: sevColor(p.severity), background: `${sevColor(p.severity)}1a` }}>{p.status}</span>
                        {rc && <span className="text-[9px] font-mono shrink-0" style={{ color: rc.soon ? "#FFB020" : "#64748B" }}>{rc.days >= 0 ? `${rc.days}d` : "—"}</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="text-center mt-4 pt-3 border-t border-[#232D42]">
          <a href="https://sidelineiq.io" target="_blank" rel="noopener noreferrer"
            className="text-[10px] font-mono text-slate-600 hover:text-slate-400">
            Powered by <span style={{ color }}>SidelineIQ</span>
          </a>
        </div>
      </div>
    </div>
  );
}
